import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { discountsApi, profileApi } from '@pukka/api';
import { clickstreamTracker } from '@pukka/clickstream';
import { toast } from '@pukka/toast';
import { Button, ScrollArea } from '@pukka/ui-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import DiscountForm from './DiscountForm';
import { DiscountCreateFormSchema, DiscountCreateFormSchemaType } from './discountFormSchema';
import discountState from './discountStore';

interface DiscountCreateBoxProps {
  readonly onClose: () => void;
}

function DiscountCreateBox({ onClose }: DiscountCreateBoxProps) {
  const { data: profile, isLoading: fetchingProfile } = profileApi.useProfile();

  const methods = useForm<DiscountCreateFormSchemaType>({
    defaultValues: {
      name: '',
      outlet_ids: [],
      category: 'fixedAmount',
      amountType: 'cash',
      discountTypeCustom: 'cash',
      amount: undefined,
    },
    resolver: zodResolver(DiscountCreateFormSchema),
  });

  const { handleSubmit, setValue, setError, reset } = methods;

  const { mutate: createDiscount, isLoading: creatingDiscount } = useMutation(discountsApi.createDiscount, {
    onSuccess: (data, variables) => {
      discountState.dirtyFormExists = false;
      reset();
      toast.success('Discount has been created');
      onClose();
      discountsApi.invalidateDiscounts();

      clickstreamTracker.trackComponent('MOKAPOS_LIBRARY_DISCOUNT_CREATE', {
        merchant: {
          businessId: profile?.business?.id,
        },
        changes: [
          {
            name: 'Name',
            value: variables.name,
          },
          {
            name: 'Value',
            value: variables.amount?.toString(),
          },
          {
            name: 'Type',
            value: variables.type,
          },
          {
            name: 'Outlet Assign',
            value: variables.outlet_ids.length?.toString(),
          },
        ],
      });
    },
    onError: async (error: AxiosError<discountsApi.MutationError>) => {
      toast.error(error.message);

      if (error.response?.data?.code === '0005-0010') {
        setError('name', {
          type: 'custom',
          message: error.message,
        });
      }
    },
  });

  const onSubmit = handleSubmit((data) => {
    const body = {
      amount: data.category === 'fixedAmount' ? Number(data.amount) : null,
      name: data.name,
      outlet_ids: data.outlet_ids,
      type: (data.category === 'fixedAmount' ? data.amountType : data.discountTypeCustom) || 'cash',
    };

    createDiscount(body);
  });

  useEffect(() => {
    reset();

    if (profile?.outlets?.length === 1) {
      setValue('outlet_ids', [profile.outlets?.[0].id ?? 0]);
    }
  }, [profile, setValue, reset]);

  return (
    <>
      <form name="discount-create" className="relative h-full overflow-auto">
        <ScrollArea className="flex min-h-full flex-col overflow-auto bg-neutral-100 p-5 pb-32 md:pb-20">
          <div className="h-full rounded border border-neutral-200 bg-white">
            <div className="border-b border-neutral-200 px-3 py-2.5 text-sm font-medium text-neutral-500">DISCOUNT INFORMATION</div>
            <FormProvider {...methods}>
              <DiscountForm />
            </FormProvider>
          </div>
        </ScrollArea>
      </form>
      <div className="absolute bottom-0 flex w-full items-center justify-between gap-x-2 bg-neutral-200 px-4 py-2.5">
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={fetchingProfile || creatingDiscount} onClick={onSubmit}>
          Create
        </Button>
      </div>
    </>
  );
}

export default DiscountCreateBox;
